<template>
  <div class="'cf-container-full-width cf-flex cf-flex--column cf-flex--align-center cf-flex--justify-center'">
    <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-max-width-600">
      <div class="cf-card cf-flex cf-flex--column cf-flex--gap-16">
        <template v-if="isQrCodeEnabled">
          <cf-loading-wrapper
            :is-loading="isLoadingQrCode"
            :loader-type="'section'"
          >
            <div class="qr-code-container">
              <vue-qrcode
                v-if="qrCode"
                class="cf-image cf-image--auto-max cf-image--border"
                :value="qrCode"
                :scale="4"
              />
            </div>
          </cf-loading-wrapper>

          <p
            v-if="qrCode"
            class="cf-subtitle cf-subtitle--2 cf-text-align-center"
          >
            Code will refresh in: <span class="cf-subtitle--heavy">{{ codeExpirationDisplayTime }}</span>
          </p>
        </template>

        <div
          v-if="isQrCodeValueEnabled"
          class="cf-flex cf-flex--column cf-flex--align-center cf-flex--justify-center cf-flex--gap-8"
        >
          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            {{ content.ScanToCheckInQrCode.metadata.codeSubtitle }}
          </p>

          <p class="cf-subtitle cf-subtitle--2">
            {{ qrCode }}
          </p>
        </div>
      </div>

      <!-- Rewards / Offers -->
      <cf-loading-wrapper
        v-if="singleScanOfferEnabled"
        :is-loading="isFetchingRedemptionCode"
        :loader-type="'section'"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-12">
          <cf-verify-account-cta />

          <cf-offers-card-list
            :applied-offer="appliedOffer"
            :offers="offers.allOffers"
            @apply-offer="applyOffer"
            @remove-offer="removeOffer"
          />
        </div>
      </cf-loading-wrapper>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import moment from 'moment-timezone';

  import http from '@/services/http';

  import errorHandlers from '@/services/error-handlers';

  import CfOffersCardList from '../cf-offers-card-list/index.vue';

  import CfLoadingWrapper from '@/view/components/globals/cf-loading-wrapper/index.vue';
  import CfVerifyAccountCta from '@/view/components/globals/cf-verify-account-cta/index.vue';


  export default {
    name: 'ScanOnlyPage',

    components: {
      CfOffersCardList,
      CfLoadingWrapper,
      CfVerifyAccountCta
    },

    data() {
      return {
        isLoadingQrCode: false,
        qrCode: '',
        codeExpirationInterval: null,
        secondsUntilCodeExpiration: 0,
        codeExpirationTimestamp: null,
        appliedOffer: {},
        isFetchingRedemptionCode: false,
        offerRedemptionCode: null,
        offerKeyToken: null,
        realTimeOrders: null
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('offers', ['loyalty', 'offers']),
      ...mapGetters('session', ['currentUser']),
      ...mapGetters('locations', ['timeZone']),
      ...mapGetters('merchant', ['merchant', 'shouldUseOfferKeyToken', 'shouldGenerateRedemptionCode']),

      isQrCodeEnabled() {
        const { displayMemberCheckinTokenQrCode } = this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions ?? {};

        return displayMemberCheckinTokenQrCode;
      },

      isQrCodeValueEnabled() {
        const { displayMemberCheckinTokenText } = this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions ?? {};

        return displayMemberCheckinTokenText;
      },

      singleScanOfferEnabled() {
        return this.merchant.features['single-scan-enable-activate-offer-option'];
      },

      hasOffers() {
        return !!this.offers.allOffers.length;
      },

      offerThresholds() {
        return this.loyalty.unlockedThresholds;
      },

      codeExpirationDisplayTime() {
        if (this.secondsUntilCodeExpiration === 0) {
          return '0:00';
        }

        const minutes = Math.floor(this.secondsUntilCodeExpiration / 60);
        const seconds = this.secondsUntilCodeExpiration - (minutes * 60);
        const secondsPadded = seconds < 10 ? `0${seconds}` : seconds;

        return `${minutes}:${secondsPadded}`;
      }
    },

    watch: {
      appliedOffer: 'getQrCode',

      realTimeOrders(newOrders, oldOrders) {
        /**
         * The `oldOrders` value will start as `null` before receiving the initial state of
         * the user's orders from the real-time database. When checking for new orders, we
         * must ensure that the `oldOrders` value has been set with the initial orders
         * before comparing the amount of new vs old orders.
         */
        const hasNewOrder = !!oldOrders && oldOrders.length < newOrders.length;

        if (hasNewOrder) {
          const latestOrder = newOrders[newOrders.length - 1];

          this.$openModal(
            'scanToPayReceiptModal',
            {
              receiptToken: latestOrder.ReceiptToken,
              confirmHandler: this.refreshData
            },
            { isRequired: true }
          );
        }
      }
    },

    mounted() {
      this.mountedHandler();
    },

    created() {
      this.onCreated();
    },


    beforeUnmount() {
      this.beforeDestroyHandler();
    },

    unmounted() {
      this.stopCodeExpirationTimer();
    },

    methods: {
      onCreated() {
        if (this.$route.query?.offerAllocationPublicId) {
          this.handleRouteOffer();
        }
        else if (this.$route.query?.thresholdPublicId) {
          this.handleRouteThreshold();
        }
        this.getQrCode();
      },

      async mountedHandler() {
        if (this.$realTimeOrdersDatabase.isConfigurationAvailable()) {
          await this.$realTimeOrdersDatabase.connect(this.observeRealTimeDb.bind(this));
        }

        if (this.$route.query?.offerAllocationPublicId) {
          this.handleRouteOffer();
        }
        else if (this.$route.query?.thresholdPublicId) {
          this.handleRouteThreshold();
        }
      },

      observeRealTimeDb(observableData) {
        this.realTimeOrders = observableData ? Object.values(observableData) : [];
      },

      beforeDestroyHandler() {
        if (this.$realTimeOrdersDatabase.isConfigurationAvailable()) {
          this.$realTimeOrdersDatabase.disconnect();
        }
      },

      handleRouteOffer() {
        const { offerAllocationPublicId } = this.$route.query;

        const selectedOffer = this.offers.allOffers.find(offer => offer.offerAllocationPublicId === offerAllocationPublicId);

        this.applyOffer(selectedOffer);
      },

      handleRouteThreshold() {
        const { thresholdPublicId } = this.$route.query;
        const selectedThreshold = this.offerThresholds.find(threshold => threshold.publicId === thresholdPublicId);

        this.applyOffer({
          ...selectedThreshold.rewardOffer,
          offerKeyToken: selectedThreshold.offerKeyToken,
          thresholdPublicId
        });
      },

      async getQrCode() {
        this.isLoadingQrCode = true;

        this.stopCodeExpirationTimer();

        const currentUserAccountId = this.currentUser.accountId;

        try {
          const requestBody = {};

          if (this.offerRedemptionCode) Object.assign(requestBody, { offerRedemptionCode: this.offerRedemptionCode });
          if (this.offerKeyToken) Object.assign(requestBody, { offerKeyToken: this.offerKeyToken });

          const { data } = await http.post(`/cardfree-api/v1/account/${currentUserAccountId}/single-scan-check-in-tokens`, requestBody);

          this.qrCode = data.checkinToken;

          this.codeExpirationTimestamp = data.expirationDate;

          this.startCodeExpirationTimer();
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isLoadingQrCode = false;
        }
      },

      startCodeExpirationTimer() {
        const now = moment();
        const expiration = moment(this.codeExpirationTimestamp);
        this.secondsUntilCodeExpiration = Math.ceil(expiration.diff(now, 'seconds', true));
        this.codeExpirationInterval = setInterval(this.codeExpirationTimerTick, 1000);
      },

      stopCodeExpirationTimer() {
        clearInterval(this.codeExpirationInterval);
      },

      codeExpirationTimerTick() {
        this.secondsUntilCodeExpiration--;

        if (this.secondsUntilCodeExpiration === 0) {
          this.getQrCode();
        }
      },

      async applyOffer(offer) {
        this.isFetchingRedemptionCode = true;
        try {
          // For the new offer key token flow, we don't need to generate redemption codes
          if (this.shouldUseOfferKeyToken) {
            // The offer should already contain offerKeyToken so just set it, no API call needed
            this.offerRedemptionCode = null;
            this.offerKeyToken = offer.offerKeyToken;
          }
          else if (this.shouldGenerateRedemptionCode) {
            // Legacy flow - generate redemption code
            const payload = {
              ianaTimezoneId: this.timeZone,
              offerAllocationPublicId: offer.offerAllocationPublicId
            };

            const { data } = await http.post('/cardfree-api/v1/offer/me/redemption-codes', payload);

            this.offerRedemptionCode = data.redemptionCode;
            this.offerKeyToken = null;
          }
          this.appliedOffer = offer;

          let routerPayload;
          if (offer.offerAllocationPublicId) {
            routerPayload = { offerAllocationPublicId: offer.offerAllocationPublicId };
          }
          else if (offer.thresholdPublicId) {
            routerPayload = { thresholdPublicId: offer.thresholdPublicId };
          }

          if (JSON.stringify(this.$route.query) !== JSON.stringify(routerPayload)) {
            this.$router.replace({
              query: { ...routerPayload }
            });
          }
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isFetchingRedemptionCode = false;
        }
      },

      removeOffer() {
        this.appliedOffer = {};

        this.offerRedemptionCode = '';
        this.offerKeyToken = null;
        this.$router.replace({ query: {} });
      }
    }

  };
</script>

<style lang="sass" scoped>
.qr-code-container
  display: flex
  align-items: center
  justify-content: center
  min-height: 140px
</style>

